import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DayPicker } from 'react-day-picker';
import {
  format as formatDate,
  addDays,
  isSameDay,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-day-picker/style.css';
import './date-picker.css';

import { useIsMobile } from '../hooks/useIsMobile';
import { useClickOutside } from '../hooks/useClickOutside';
import { useEscape } from '../hooks/useEscape';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { toUtcDayEnd, toUtcDayStart } from '../utils/dateUtils';
import { ChevronDown } from './icons/ChevronDown';

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const presets = [
  {
    key: 'today',
    label: 'Today',
    range: () => {
      const today = startOfDay(new Date());
      return { from: today, to: today };
    },
  },
  {
    key: 'yesterday',
    label: 'Yesterday',
    range: () => {
      const y = startOfDay(addDays(new Date(), -1));
      return { from: y, to: y };
    },
  },
  {
    key: 'thisWeek',
    label: 'This Week',
    range: () => {
      const now = new Date();
      return {
        from: startOfWeek(now, { weekStartsOn: 1 }),
        to: endOfWeek(now, { weekStartsOn: 1 }),
      };
    },
  },
  {
    key: 'last7Days',
    label: 'Last 7 Days',
    range: () => {
      const today = startOfDay(new Date());
      return { from: addDays(today, -6), to: today };
    },
  },
  {
    key: 'thisMonth',
    label: 'This Month',
    range: () => {
      const now = new Date();
      return { from: startOfMonth(now), to: endOfMonth(now) };
    },
  },
  {
    key: 'last30Days',
    label: 'Last 30 Days',
    range: () => {
      const today = startOfDay(new Date());
      return { from: addDays(today, -29), to: today };
    },
  },
];

const matchPreset = (range) => {
  if (!range?.from || !range?.to) return undefined;
  return presets.find((p) => {
    const r = p.range();
    return isSameDay(r.from, range.from) && isSameDay(r.to, range.to);
  })?.key;
};

const formatLocalRange = (start, end) => {
  if (!start || !end) return '';
  if (isSameDay(start, end)) return formatDate(start, 'MMM d, yyyy');
  if (start.getFullYear() === end.getFullYear()) {
    return `${formatDate(start, 'MMM d')} - ${formatDate(end, 'MMM d, yyyy')}`;
  }
  return `${formatDate(start, 'MMM d, yyyy')} - ${formatDate(end, 'MMM d, yyyy')}`;
};

const formatRangeForDisplay = (startStr, endStr) => {
  if (!startStr || !endStr) return '';
  const start = new Date(startStr);
  const end = new Date(endStr);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return '';

  const sYear = start.getUTCFullYear();
  const sMonth = start.getUTCMonth();
  const sDay = start.getUTCDate();
  const eYear = end.getUTCFullYear();
  const eMonth = end.getUTCMonth();
  const eDay = end.getUTCDate();

  const sLocal = new Date(sYear, sMonth, sDay);
  const eLocal = new Date(eYear, eMonth, eDay);
  const currentYear = new Date().getFullYear();

  if (sYear === eYear && sMonth === eMonth && sDay === eDay) {
    return sYear === currentYear
      ? formatDate(sLocal, 'MMM dd')
      : formatDate(sLocal, 'MMM dd, yyyy');
  }
  if (sYear === eYear && sYear === currentYear) {
    return `${formatDate(sLocal, 'MMM dd')} - ${formatDate(eLocal, 'MMM dd')}`;
  }
  if (sYear === eYear) {
    return `${formatDate(sLocal, 'MMM dd')} - ${formatDate(eLocal, 'MMM dd, yyyy')}`;
  }
  return `${formatDate(sLocal, 'MMM dd, yyyy')} - ${formatDate(eLocal, 'MMM dd, yyyy')}`;
};

const getLocalDateFromUTC = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

const PRESET_BUTTON_BASE =
  'cursor-pointer rounded border-none bg-transparent px-3 py-2 text-left text-sm transition-colors';

export default function CustomDateRangePicker({ startDate, endDate, onChange, className }) {
  const [isOpen, setIsOpen] = useState(false);
  // Pending range — set while the picker is open and the user is clicking
  // dates. Only committed (via onChange) when the picker closes, so we don't
  // fire a request on every intermediate click.
  const [pendingRange, setPendingRange] = useState(null);
  const { isMobile: isSmallScreen } = useIsMobile();
  const containerRef = useRef(null);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);

  const selectedRangeRef = useRef(null);
  const pendingRangeRef = useRef(null);
  const onChangeRef = useRef(onChange);

  const commitAndClose = useCallback(() => {
    const pending = pendingRangeRef.current;
    const selected = selectedRangeRef.current;
    if (pending?.from) {
      const from = pending.from;
      const to = pending.to ?? pending.from;
      const changed = !selected || !isSameDay(from, selected.from) || !isSameDay(to, selected.to);
      if (changed) {
        onChangeRef.current(
          toUtcDayStart(new Date(from)).toISOString(),
          toUtcDayEnd(new Date(to)).toISOString(),
          matchPreset({ from, to }),
        );
      }
    }
    setPendingRange(null);
    setIsOpen(false);
  }, []);

  useClickOutside([containerRef, pickerRef], commitAndClose, isOpen);
  useEscape(commitAndClose, isOpen);
  // Only the mobile sheet is aria-modal; the desktop popover leaves the page
  // behind it reachable on purpose.
  useFocusTrap(pickerRef, isOpen && isSmallScreen);

  useEffect(() => {
    if (isOpen && isSmallScreen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen, isSmallScreen]);

  const selectedRange = useMemo(() => {
    const from = getLocalDateFromUTC(startDate) || addDays(new Date(), -7);
    const to = getLocalDateFromUTC(endDate) || new Date();
    return { from, to };
  }, [startDate, endDate]);

  // Mirror the latest values for commitAndClose (an event handler) to read.
  // Assigning during render is unsafe under concurrent rendering.
  useEffect(() => {
    selectedRangeRef.current = selectedRange;
    pendingRangeRef.current = pendingRange;
    onChangeRef.current = onChange;
  });

  // While the picker is open we display the pending (in-progress) range;
  // while closed, mirror selectedRange.
  const displayRange = pendingRange ?? selectedRange;

  // Controlled current month. Re-synced when the picker opens (see openPicker)
  // rather than from an effect, so DayPicker doesn't re-render an extra time
  // per hover/click — that is expensive given DayPicker's internal cost.
  const [visibleMonth, setVisibleMonth] = useState(() => selectedRange.from || new Date());

  const handleSelect = useCallback((range) => {
    setPendingRange(range || null);
  }, []);

  const openPicker = useCallback(() => {
    const from = selectedRangeRef.current?.from;
    if (from) setVisibleMonth(from);
    setIsOpen(true);
  }, []);

  const closeAndRefocus = useCallback(() => {
    commitAndClose();
    triggerRef.current?.focus();
  }, [commitAndClose]);

  // Presets are explicit "commit-and-close" actions.
  const applyPreset = useCallback((preset) => {
    const r = preset.range();
    setPendingRange(null);
    onChangeRef.current(
      toUtcDayStart(new Date(r.from)).toISOString(),
      toUtcDayEnd(new Date(r.to)).toISOString(),
      preset.key,
    );
    setIsOpen(false);
  }, []);

  const isPresetActive = (preset) => {
    const r = preset.range();
    return isSameDay(r.from, displayRange.from) && isSameDay(r.to, displayRange.to);
  };

  const calendar = (
    <DayPicker
      mode="range"
      selected={displayRange}
      onSelect={handleSelect}
      month={visibleMonth}
      onMonthChange={setVisibleMonth}
      numberOfMonths={1}
      weekStartsOn={1}
      locale={enUS}
      showOutsideDays
      className="rdp-themed"
    />
  );

  const pickerContent = isSmallScreen ? (
    <>
      <div
        className="fixed inset-0 z-[1400] animate-fade-in bg-black/[0.78]"
        onClick={commitAndClose}
      />
      <div className="relative z-[1501] pb-4">
        <div className="flex flex-col bg-surface-raised">
          <div className="flex justify-center pt-2 pb-1">
            <div className="h-1 w-9 rounded-sm bg-line opacity-60" />
          </div>
          <div className="flex items-center justify-between px-4 pt-1.5 pb-2.5">
            <span className="mr-3 truncate text-[0.95rem] font-semibold text-fg">
              {formatLocalRange(displayRange.from, displayRange.to)}
            </span>
            <button
              type="button"
              onClick={closeAndRefocus}
              className="shrink-0 rounded-sm px-3.5 py-1.5 text-[0.95rem] font-semibold text-accent"
            >
              Done
            </button>
          </div>
          <div className="hide-scrollbar flex gap-2 overflow-x-auto border-b border-line px-4 pt-1 pb-3">
            {presets.map((preset) => {
              const active = isPresetActive(preset);
              return (
                <button
                  key={preset.key}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  aria-pressed={active}
                  className={`shrink-0 rounded-full border px-3.5 py-[7px] text-[0.85rem] font-medium whitespace-nowrap transition-colors ${
                    active
                      ? 'border-accent bg-accent text-white'
                      : 'border-line bg-transparent text-fg-muted'
                  }`}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex justify-center py-2">{calendar}</div>
      </div>
    </>
  ) : (
    <div className="relative z-[1501] flex items-stretch">
      <div className="flex w-[150px] min-w-[150px] flex-col gap-0.5 border-r border-line bg-surface py-2">
        {presets.map((preset) => {
          const active = isPresetActive(preset);
          return (
            <button
              key={preset.key}
              type="button"
              onClick={() => applyPreset(preset)}
              aria-pressed={active}
              className={`${PRESET_BUTTON_BASE} mx-2 ${
                active
                  ? 'bg-accent-strong font-semibold text-accent'
                  : 'text-fg-muted hover:bg-surface-elevated hover:text-fg'
              }`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
      <div className="p-2">{calendar}</div>
    </div>
  );

  return (
    <div className={`relative z-[900] w-full ${className ?? ''}`} ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (isOpen ? commitAndClose() : openPicker())}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        className={`flex h-9 w-full cursor-pointer items-center justify-between rounded-sm border bg-surface-raised px-2.5 text-left text-[0.9rem] text-fg transition-colors ${
          isOpen ? 'border-accent' : 'border-line'
        }`}
      >
        <span className="mr-2 min-w-0 truncate">{formatRangeForDisplay(startDate, endDate)}</span>
        <ChevronDown className="shrink-0" />
      </button>

      {isOpen &&
        (isSmallScreen ? (
          createPortal(
            <div
              ref={pickerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Select date range"
              tabIndex={-1}
              className="fixed inset-x-0 bottom-0 z-[1500] max-h-[90vh] w-full animate-slide-up touch-manipulation overflow-y-auto rounded-t-xl border-t border-line bg-surface-raised shadow-sheet"
            >
              {pickerContent}
            </div>,
            document.body,
          )
        ) : (
          <div
            ref={pickerRef}
            role="dialog"
            aria-label="Select date range"
            className="absolute top-[calc(100%+8px)] right-0 z-[950] w-auto animate-fade-in overflow-hidden rounded-md border border-line bg-surface-raised shadow-medium"
          >
            {pickerContent}
          </div>
        ))}
    </div>
  );
}
